// src/components/Navbar.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useFavorites } from "../context/FavoritesContext";
import { getAuth, signOut } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import {
  FaBars, FaBoxes, FaList, FaPercent, FaSignOutAlt, FaTimes, FaClipboardList, FaChevronRight, FaUser, FaUsers, FaShoppingBag, FaPlus, FaCommentDots, FaImage, FaBoxOpen, FaUndo, FaCog, FaChartBar, FaClipboardCheck, FaHeart, FaRegHeart, FaChevronDown, FaTicketAlt, FaBullhorn, FaGavel
} from "react-icons/fa";

import { db } from "../firebase";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";

function Navbar({ user }) {
  const { cartItems } = useCart();
  const { favorites } = useFavorites();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);

  const [showMega, setShowMega] = useState(false);
  const [hoveredTop, setHoveredTop] = useState(null);
  const hideTimerRef = useRef(null);

  const [pendingReturnsCount, setPendingReturnsCount] = useState(0);
  const [newOrdersCount, setNewOrdersCount] = useState(0);

  const [clearedOrders, setClearedOrders] = useState(() => parseInt(localStorage.getItem('clearedOrders') || '0'));
  const [clearedReturns, setClearedReturns] = useState(() => parseInt(localStorage.getItem('clearedReturns') || '0'));

  const [realUserName, setRealUserName] = useState("");

  const adminPaths = ["/admin"];
  const isAdminPage = adminPaths.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    const fetchRealName = async () => {
      if (user && user.uid) {
        try {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && docSnap.data().fullName) {
            setRealUserName(docSnap.data().fullName);
          } else {
            setRealUserName(user.displayName || "Kullanıcı");
          }
        } catch (error) {
          setRealUserName(user.displayName || "Kullanıcı");
        }
      }
    };
    fetchRealName();
  }, [user]);

  useEffect(() => {
    setLoadingCats(true);
    const colRef = collection(db, "categories");
    const unsubscribe = onSnapshot(colRef, (snapshot) => {
      if (!snapshot.empty) {
        const cats = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        cats.sort((a, b) => (a.order || 999) - (b.order || 999));
        setCategories(cats);
        if (cats.length > 0 && !hoveredTop) {
          setHoveredTop(cats[0].slug);
        }
      } else {
        setCategories([]);
      }
      setLoadingCats(false);
    }, (error) => {
      console.error("Navbar kategori hatası:", error);
      setLoadingCats(false);
    });

    return () => unsubscribe();
  }, [hoveredTop]);

  useEffect(() => {
    if (!isAdmin) return;

    const qReturns = query(collection(db, "returnRequests"), where("status", "==", "pending_review"));
    const unsubReturns = onSnapshot(qReturns, (snapshot) => {
      const size = snapshot.size;
      setPendingReturnsCount(size);
      if (size < parseInt(localStorage.getItem('clearedReturns') || '0')) {
        localStorage.setItem('clearedReturns', size.toString());
        setClearedReturns(size);
      }
    });

    const qOrders = query(collection(db, "orders"), where("status", "==", "Sipariş Alındı"));
    const unsubOrders = onSnapshot(qOrders, (snapshot) => {
      const size = snapshot.size;
      setNewOrdersCount(size);
      if (size < parseInt(localStorage.getItem('clearedOrders') || '0')) {
        localStorage.setItem('clearedOrders', size.toString());
        setClearedOrders(size);
      }
    });

    return () => {
      unsubReturns();
      unsubOrders();
    };
  }, [isAdmin]);

  // 🔥 ÇÖZÜM: MOBİL YAYLANMA (RUBBER BANDING) GÜVENLİK KİLİDİ
  useEffect(() => {
    const onScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Eğer sayfanın en tepesinde (ilk 50 piksel) isek, menüyü ZORLA AÇIK TUT.
      // Eksi değerler (iOS yaylanması) bile gelse menü kapanmayacak.
      if (currentScrollY <= 50) {
        setShowNavbar(true);
      } 
      // Aşağı kaydırıyorsa menüyü sakla, yukarı kaydırıyorsa göster
      else if (currentScrollY < lastScrollY) {
        setShowNavbar(true);
      } else {
        setShowNavbar(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastScrollY]);

  const openMega = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setShowMega(true);
  };

  const closeMegaDelayed = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowMega(false), 200);
  };

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth).then(() => {
      setMobileOpen(false);
      navigate("/");
    });
  };

  const goCategory = (topSlug, subSlug = null, childSlug = null) => {
    let url = "/shop";
    if (topSlug) url += `/${encodeURIComponent(topSlug)}`;
    if (subSlug) url += `/${encodeURIComponent(subSlug)}`;
    if (childSlug) url += `?subs=${encodeURIComponent(childSlug)}`;
    navigate(url);
    setShowMega(false);
    setMobileOpen(false);
  };

  const handleOrdersClick = () => {
    localStorage.setItem('clearedOrders', newOrdersCount.toString());
    setClearedOrders(newOrdersCount);
    setMobileOpen(false);
  };

  const handleReturnsClick = () => {
    localStorage.setItem('clearedReturns', pendingReturnsCount.toString());
    setClearedReturns(pendingReturnsCount);
    setMobileOpen(false);
  };

  const otherMenu = [
    { to: "/indirimler", label: "Fırsat Ürünleri", isSpecial: true },
    { to: "/iletisim", label: "İletişim" },
    { to: "/hakkimizda", label: "Hakkımızda" },
    { to: "/sss", label: "SSS" },
  ];

  const menuItems = isAdminPage ? [] : otherMenu;

  const activeCategory = useMemo(() => {
    return categories.find(c => c.slug === hoveredTop);
  }, [categories, hoveredTop]);

  const displayOrdersCount = Math.max(0, newOrdersCount - clearedOrders);
  const displayReturnsCount = Math.max(0, pendingReturnsCount - clearedReturns);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 shadow-sm border-b z-50 transition-transform duration-300 bg-white ${showNavbar ? "translate-y-0" : "-translate-y-full"
          }`}
        style={{ borderBottomColor: "rgba(106, 13, 173, 0.08)" }}
      >
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-[72px] flex items-center justify-between">

          {/* LOGO */}
          <div className="flex-shrink-0 w-[200px]">
            <Link to="/" className="flex flex-col select-none cursor-pointer group">
              <span className="font-extrabold tracking-tight text-3xl bg-gradient-to-r from-[#6A0DAD] to-[#9d4edd] bg-clip-text text-transparent group-hover:opacity-90 transition">
                SenaBebe
              </span>
              <span className="text-[#6A0DAD] text-[11px] font-medium tracking-wide opacity-80 pl-0.5">
                Minik Kalplerin Büyük Dünyası
              </span>
            </Link>
          </div>

          {/* ORTA MENÜ */}
          <div className="hidden lg:flex flex-1 justify-center items-center h-full">
            <div className="flex items-center gap-8 lg:gap-10 text-[14px] lg:text-[15px] font-bold text-gray-600 h-full">
              <NavLink to="/" className={({ isActive }) => `h-full flex items-center border-b-[3px] transition-all ${isActive ? "border-[#6A0DAD] text-[#6A0DAD]" : "border-transparent hover:text-[#6A0DAD]"}`}>
                Anasayfa
              </NavLink>

              <div
                className="relative h-full flex items-center"
                onMouseEnter={openMega}
                onMouseLeave={closeMegaDelayed}
              >
                <button className={`flex items-center gap-1 h-full border-b-[3px] transition-all ${showMega ? "border-[#6A0DAD] text-[#6A0DAD]" : "border-transparent hover:text-[#6A0DAD]"}`}>
                  Kategoriler
                  <FaChevronDown size={10} className={`transition-transform duration-300 ${showMega ? "rotate-180" : ""}`} />
                </button>

                {showMega && (
                  <div
                    className="fixed top-[72px] left-1/2 -translate-x-1/2 w-[95vw] max-w-[1100px] bg-white shadow-2xl rounded-xl border border-gray-100 overflow-hidden flex min-h-[480px] animate-fade-in z-[60]"
                    style={{ boxShadow: "0 20px 40px -10px rgba(106, 13, 173, 0.15)" }}
                    onMouseEnter={openMega}
                    onMouseLeave={closeMegaDelayed}
                  >
                    <div className="w-[280px] bg-gray-50/80 border-r border-gray-100 py-6 overflow-y-auto max-h-[600px]">
                      {loadingCats ? (
                        <div className="p-8 text-center text-gray-400 text-sm">Yükleniyor...</div>
                      ) : (
                        categories.map((cat) => (
                          <div
                            key={cat.slug}
                            onMouseEnter={() => setHoveredTop(cat.slug)}
                            onClick={() => goCategory(cat.slug)}
                            className={`px-8 py-3.5 cursor-pointer flex items-center justify-between text-sm font-bold transition-all relative ${hoveredTop === cat.slug
                              ? "bg-white text-[#6A0DAD] shadow-sm"
                              : "text-gray-600 hover:text-gray-900"
                              }`}
                          >
                            {hoveredTop === cat.slug && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#6A0DAD]"></div>}
                            <span>{cat.label}</span>
                            <FaChevronRight size={10} className={`transition-opacity ${hoveredTop === cat.slug ? "opacity-100" : "opacity-0"}`} />
                          </div>
                        ))
                      )}
                    </div>

                    <div className="flex-1 p-10 bg-white overflow-y-auto max-h-[600px]">
                      {activeCategory ? (
                        <div className="animate-fade-in">
                          <div className="flex items-end justify-between mb-8 pb-4 border-b border-gray-100">
                            <div>
                              <h3 className="text-2xl font-extrabold text-gray-800 tracking-tight">
                                {activeCategory.label}
                              </h3>
                            </div>
                            <button
                              onClick={() => goCategory(activeCategory.slug)}
                              className="text-sm font-bold text-[#6A0DAD] hover:bg-purple-50 px-4 py-2 rounded-lg transition"
                            >
                              Tüm Ürünleri Gör →
                            </button>
                          </div>

                          <div className="grid grid-cols-3 gap-x-12 gap-y-6">
                            {activeCategory.children && activeCategory.children.length > 0 ? (
                              activeCategory.children.map((sub, idx) => (
                                <div key={idx} className="break-inside-avoid">
                                  <button
                                    onClick={() => goCategory(activeCategory.slug, sub.slug)}
                                    className="font-bold text-gray-700 mb-2 text-[14px] hover:text-[#6A0DAD] block w-full text-left"
                                  >
                                    {sub.label}
                                  </button>

                                  {sub.children && sub.children.length > 0 && (
                                    <ul className="space-y-1.5 pl-1 border-l-2 border-gray-100 mt-1 mb-4">
                                      {sub.children.map((subSub) => (
                                        <li key={subSub.slug}>
                                          <button
                                            className="text-[13px] text-gray-500 hover:text-[#6A0DAD] hover:translate-x-1 transition-all block w-full text-left py-0.5"
                                            onClick={(e) => { e.stopPropagation(); goCategory(activeCategory.slug, sub.slug, subSub.slug); }}
                                          >
                                            {subSub.label}
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              ))
                            ) : (
                              <p className="text-gray-400 text-sm col-span-3">Bu kategoride henüz alt kategori bulunmuyor.</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          Kategori seçiniz.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {menuItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    item.isSpecial
                      ? `flex items-center h-[36px] my-auto px-4 rounded-lg bg-[#6A0DAD]/10 text-[#6A0DAD] hover:bg-[#6A0DAD]/20 transition-all ${isActive ? "ring-2 ring-[#6A0DAD]/30" : ""}`
                      : `h-full flex items-center border-b-[3px] transition-all ${isActive ? "border-[#6A0DAD] text-[#6A0DAD]" : "border-transparent hover:text-[#6A0DAD]"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>

          {/* SAĞ TARAF: KULLANICI İŞLEMLERİ */}
          <div className="flex items-center justify-end gap-2 lg:gap-4 flex-shrink-0">
            <div className="hidden lg:flex items-center gap-2 lg:gap-4 text-sm font-semibold text-gray-700">

              {!user ? (
                <div className="relative group flex items-center">
                  <button className="flex items-center gap-1.5 hover:text-[#6A0DAD] py-2 transition font-semibold text-gray-700">
                    <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-500 group-hover:text-[#6A0DAD] group-hover:border-purple-200 group-hover:bg-purple-50 transition-all flex-shrink-0">
                      <FaUser size={13} className="lg:text-[14px]" />
                    </div>
                    <span className="whitespace-nowrap hidden xl:block">Giriş Yap</span>
                  </button>

                  <div className="absolute top-full right-0 w-60 bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden hidden group-hover:block pt-2 animate-fade-in z-50">
                    <div className="flex flex-col text-left py-3 px-4 gap-2.5">
                      <Link to="/login" className="w-full text-center bg-[#6A0DAD] text-white px-4 py-2.5 rounded-xl font-bold text-sm shadow-md hover:bg-purple-800 transition">
                        Giriş Yap
                      </Link>
                      <Link to="/register" className="w-full text-center bg-white text-[#6A0DAD] border border-purple-200 px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-purple-50 transition">
                        Üye Ol
                      </Link>
                    </div>
                    <div className="border-t border-gray-100 mt-1">
                      <Link to="/kargo-takip" className="px-5 py-3.5 hover:bg-gray-50 text-gray-700 transition flex items-center gap-3 font-medium text-sm">
                        <FaBoxOpen className="text-[#6A0DAD] text-lg" /> Sipariş Takibi
                      </Link>
                      <Link to="/sozlesmeler" className="px-5 py-3.5 hover:bg-gray-50 text-gray-700 transition flex items-center gap-3 font-medium text-sm border-t border-gray-50">
                        <FaGavel className="text-gray-400 text-lg" /> Yasal Bilgilendirmeler
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative group flex items-center">
                  <button className="flex items-center gap-1.5 hover:text-[#6A0DAD] py-2 transition">
                    <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-purple-50 border border-purple-100 flex items-center justify-center text-[#6A0DAD] flex-shrink-0">
                      <FaUser size={13} className="lg:text-[14px]" />
                    </div>
                    <span className="max-w-[80px] lg:max-w-[100px] truncate whitespace-nowrap hidden xl:block">Hesabım</span>
                  </button>

                  <div className="absolute top-full right-0 w-52 bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden hidden group-hover:block pt-2 animate-fade-in z-50 max-h-[70vh] overflow-y-auto">                    <div className="flex flex-col text-left py-2">
                    {!isAdmin ? (
                      <>
                        <Link to="/profile" className="px-5 py-3 hover:bg-purple-50 hover:text-[#6A0DAD] text-gray-600 transition flex items-center gap-2"><FaUser size={12} /> Profilim</Link>
                        <Link to="/my-orders" className="px-5 py-3 hover:bg-purple-50 hover:text-[#6A0DAD] text-gray-600 transition flex items-center gap-2"><FaClipboardList size={12} /> Siparişler</Link>
                        <Link to="/favorites" className="px-5 py-3 hover:bg-purple-50 hover:text-[#6A0DAD] text-gray-600 transition flex items-center gap-2 border-t border-gray-50"><FaHeart size={12} className="text-red-400" /> Favorilerim</Link>
                      </>
                    ) : (
                      <>
                        <Link to="/admin/categories" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2 border-b border-purple-50"><FaList /> Kategoriler</Link>
                        <Link to="/admin/products" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaBoxes /> Ürünler</Link>

                        <Link to="/admin/customers" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2">
                          <FaUsers /> Müşteriler
                        </Link>

                        <Link to="/admin/orders" onClick={handleOrdersClick} className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center justify-between group/order">
                          <span className="flex items-center gap-2"><FaClipboardList /> Siparişler</span>
                          {displayOrdersCount > 0 && (
                            <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full animate-pulse shadow-sm">
                              {displayOrdersCount} Yeni
                            </span>
                          )}
                        </Link>

                        <Link to="/admin/returns" onClick={handleReturnsClick} className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center justify-between group/return">
                          <span className="flex items-center gap-2"><FaUndo /> İadeler</span>
                          {displayReturnsCount > 0 && (
                            <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full animate-pulse shadow-sm">
                              {displayReturnsCount} Yeni
                            </span>
                          )}
                        </Link>
                        <Link to="/admin/dashboard" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2 border-b border-purple-50">
                          <FaChartBar /> Dashboard
                        </Link>
                        <Link to="/admin/stock-manager" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaList /> Stok</Link>
                        <Link to="/admin/stock-out" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaClipboardCheck /> Fiziksel Kasa</Link>
                        <Link to="/admin/discounts" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaPercent /> İndirimler</Link>
                        <Link to="/admin/comments" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaCommentDots /> Yorumlar</Link>
                        <Link to="/admin/sliders" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaImage /> Slider Yönetimi</Link>
                        <Link to="/admin/settings" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaCog /> Ayarlar</Link>
                        <Link to="/admin/products/new" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2 border-t border-gray-100 mt-1"><FaPlus /> Ürün Ekle</Link>
                        <Link to="/admin/coupons" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaTicketAlt /> Kuponlar</Link>
                        <Link to="/admin/campaigns" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2"><FaBullhorn /> Kampanyalar (Bülten)</Link>
                        <Link to="/admin/mail-settings" className="px-5 py-3 hover:bg-purple-50 text-[#6A0DAD] font-bold flex items-center gap-2">📧 Mail Ayarları</Link>
                      </>
                    )}
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      {!isAdmin && (
                        <Link
                          to="/sozlesmeler"
                          className="px-5 py-3 hover:bg-purple-50 hover:text-[#6A0DAD] text-gray-600 transition flex items-center gap-2"
                        >
                          <FaGavel className="text-gray-400" /> Yasal Bilgiler
                        </Link>
                      )}
                      <button onClick={handleLogout} className="w-full px-5 py-3 text-left hover:bg-red-50 text-red-600 transition flex items-center gap-2 font-medium">
                        <FaSignOutAlt /> Çıkış Yap
                      </button>
                    </div>
                  </div>
                </div>
                </div>
              )}
            </div>

            {!isAdmin && (
              <div className="hidden lg:flex items-center gap-1.5 lg:gap-3 flex-shrink-0">
                <NavLink to="/favorites" className="relative text-gray-700 hover:text-red-500 transition group p-2">
                  <FaRegHeart size={20} className="lg:text-[22px]" />
                  {favorites.length > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[10px] leading-none pt-[1px] font-bold flex items-center justify-center rounded-full ring-2 ring-white shadow-sm group-hover:scale-110 transition-transform">
                      {favorites.length > 99 ? "99+" : favorites.length}
                    </span>
                  )}
                </NavLink>

                <NavLink to="/cart" className="relative text-gray-700 hover:text-[#6A0DAD] transition group p-2">
                  <FaShoppingBag size={20} className="lg:text-[22px]" />
                  {cartItems.length > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-[#6A0DAD] text-white text-[10px] leading-none pt-[1px] font-bold flex items-center justify-center rounded-full ring-2 ring-white shadow-sm group-hover:scale-110 transition-transform">
                      {cartItems.length > 99 ? "99+" : cartItems.length}
                    </span>
                  )}
                </NavLink>
              </div>
            )}

            {/* MOBİL MENÜ İKONU */}
            <button
              className="lg:hidden text-gray-700 hover:text-[#6A0DAD] transition p-1"
              onClick={() => setMobileOpen(true)}
            >
              <FaBars size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* --- MOBİL MENÜ (Drawer) --- */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-500 ${mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>

        <div
          className={`absolute top-0 right-0 w-[85%] max-w-[320px] h-full bg-white shadow-2xl flex flex-col transition-transform duration-500 ease-in-out ${mobileOpen ? "translate-x-0" : "translate-x-full"
            }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
            <span className="font-extrabold text-xl text-[#6A0DAD] tracking-tight">SenaBebe</span>
            <button onClick={() => setMobileOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition shadow-sm">
              <FaTimes />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-6 custom-scrollbar">

            <div className="space-y-2">
              {user ? (
                <>
                  <Link to="/profile" onClick={() => setMobileOpen(false)} className="bg-purple-50 p-4 rounded-xl flex items-center gap-3 border border-purple-100 hover:bg-purple-100 transition shadow-sm mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#6A0DAD] text-white flex items-center justify-center font-bold text-lg shadow-sm">
                      {realUserName.charAt(0) || <FaUser />}
                    </div>
                    <div className="overflow-hidden flex-1">
                      <p className="text-sm font-bold text-gray-800 truncate">{realUserName}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <FaChevronRight className="text-gray-400 text-sm" />
                  </Link>

                  {!isAdminPage && (
                    <>
                      <Link to="/my-orders" onClick={() => setMobileOpen(false)} className="block px-4 py-3 rounded-lg font-semibold transition text-gray-700 hover:bg-gray-50 flex items-center gap-3">
                        <FaClipboardList className="text-gray-400" /> Siparişlerim
                      </Link>
                      <Link to="/favorites" onClick={() => setMobileOpen(false)} className="block px-4 py-3 rounded-lg font-semibold transition text-gray-700 hover:bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-3"><FaRegHeart className="text-red-400" /> Favorilerim</div>
                        {favorites.length > 0 && <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">{favorites.length}</span>}
                      </Link>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="py-3 text-center rounded-xl border border-gray-200 font-bold text-gray-600 hover:bg-gray-50 transition">Giriş Yap</Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)} className="py-3 text-center rounded-xl bg-[#6A0DAD] text-white font-bold hover:bg-purple-700 shadow-lg shadow-purple-100 transition">Kayıt Ol</Link>
                  </div>

                  {!isAdminPage && (
                    <Link to="/kargo-takip" onClick={() => setMobileOpen(false)} className="block px-4 py-3 rounded-lg font-semibold transition text-gray-700 hover:bg-gray-50 flex items-center gap-3">
                      <FaBoxOpen className="text-gray-400" /> Sipariş Takibi
                    </Link>
                  )}
                </>
              )}
            </div>

            <div>
              <h4 className="px-4 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Kategoriler</h4>
              <div className="space-y-1">
                {categories.map(cat => (
                  <details key={cat.slug} className="group rounded-lg overflow-hidden">
                    <summary className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 font-medium text-gray-700 list-none transition">
                      {cat.label}
                      <FaChevronRight size={12} className="transition-transform duration-300 group-open:rotate-90 text-gray-400" />
                    </summary>
                    <div className="bg-gray-50/50 pb-2 pt-1 border-t border-gray-50 animate-fade-in pl-4">
                      <button onClick={() => { goCategory(cat.slug); setMobileOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-[#6A0DAD] font-bold hover:bg-purple-50 transition">
                        Tümünü Gör
                      </button>
                      {cat.children?.map(sub => (
                        <div key={sub.slug}>
                          <button
                            onClick={() => { goCategory(cat.slug, sub.slug); setMobileOpen(false); }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:text-[#6A0DAD] hover:bg-white transition"
                          >
                            {sub.label}
                          </button>
                          {sub.children && sub.children.length > 0 && (
                            <div className="pl-4 border-l border-gray-200 ml-4 mb-2">
                              {sub.children.map(subSub => (
                                <button
                                  key={subSub.slug}
                                  onClick={() => { goCategory(cat.slug, sub.slug, subSub.slug); setMobileOpen(false); }}
                                  className="block w-full text-left px-2 py-1 text-xs text-gray-500 hover:text-[#6A0DAD]"
                                >
                                  - {subSub.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            </div>

            <div>
              <h4 className="px-4 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Menü</h4>
              <div className="space-y-1">
                <NavLink to="/" onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-4 py-3 rounded-lg font-semibold transition ${isActive ? "bg-purple-50 text-[#6A0DAD]" : "text-gray-700 hover:bg-gray-50"}`}>
                  Anasayfa
                </NavLink>

                {!isAdminPage && (
                  <NavLink to="/indirimler" onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-4 py-3 rounded-lg font-semibold transition ${isActive ? "bg-purple-50 text-[#6A0DAD]" : "text-gray-700 hover:bg-gray-50"} text-[#6A0DAD] bg-[#6A0DAD]/10`}>
                    Fırsat Ürünleri
                  </NavLink>
                )}

                {!isAdminPage && otherMenu.slice(1).map(item => (
                  <NavLink key={item.to} to={item.to} onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-4 py-3 rounded-lg font-semibold transition ${isActive ? "bg-purple-50 text-[#6A0DAD]" : "text-gray-700 hover:bg-gray-50"}`}>
                    {item.label}
                  </NavLink>
                ))}

                {!isAdminPage && (
                  <NavLink to="/sozlesmeler" onClick={() => setMobileOpen(false)} className={({ isActive }) => `block px-4 py-3 rounded-lg font-semibold transition ${isActive ? "bg-purple-50 text-[#6A0DAD]" : "text-gray-700 hover:bg-gray-50"}`}>
                    Yasal Bilgilendirmeler
                  </NavLink>
                )}
              </div>
            </div>

            {isAdmin && (
              <div className="pt-4 border-t border-gray-100">
                <h4 className="px-4 text-xs font-bold text-[#6A0DAD] uppercase tracking-wider mb-2">Yönetici Paneli</h4>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/admin/dashboard" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaChartBar className="mx-auto mb-1.5 text-xl opacity-70" /> Dashboard
                  </Link>
                  <Link to="/admin/categories" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaList className="mx-auto mb-1.5 text-xl opacity-70" /> Kategoriler
                  </Link>
                  <Link to="/admin/products" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaBoxes className="mx-auto mb-1.5 text-xl opacity-70" /> Ürünler
                  </Link>
                  
                  <Link to="/admin/customers" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaUsers className="mx-auto mb-1.5 text-xl opacity-70" /> Müşteriler
                  </Link>

                  <Link to="/admin/orders" onClick={handleOrdersClick} className="relative p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaClipboardList className="mx-auto mb-1.5 text-xl opacity-70" /> Siparişler
                    {displayOrdersCount > 0 && (
                      <span className="absolute top-2 right-2 bg-red-500 text-white text-[9px] w-5 h-5 flex items-center justify-center rounded-full animate-pulse shadow-sm">
                        {displayOrdersCount}
                      </span>
                    )}
                  </Link>

                  <Link to="/admin/returns" onClick={handleReturnsClick} className="relative p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaUndo className="mx-auto mb-1.5 text-xl opacity-70" /> İadeler
                    {displayReturnsCount > 0 && (
                      <span className="absolute top-2 right-2 bg-red-500 text-white text-[9px] w-5 h-5 flex items-center justify-center rounded-full animate-pulse shadow-sm">
                        {displayReturnsCount}
                      </span>
                    )}
                  </Link>

                  <Link to="/admin/stock-manager" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaList className="mx-auto mb-1.5 text-xl opacity-70" /> Stok
                  </Link>
                  <Link to="/admin/stock-out" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaClipboardCheck className="mx-auto mb-1.5 text-xl opacity-70" /> Fiziksel Kasa
                  </Link>
                  <Link to="/admin/discounts" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaPercent className="mx-auto mb-1.5 text-xl opacity-70" /> İndirim
                  </Link>
                  <Link to="/admin/comments" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaCommentDots className="mx-auto mb-1.5 text-xl opacity-70" /> Yorumlar
                  </Link>
                  <Link to="/admin/sliders" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaImage className="mx-auto mb-1.5 text-xl opacity-70" /> Slider
                  </Link>
                  <Link to="/admin/settings" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaCog className="mx-auto mb-1.5 text-xl opacity-70" /> Ayarlar
                  </Link>
                  <Link to="/admin/products/new" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaPlus className="mx-auto mb-1.5 text-xl opacity-70" /> Ürün Ekle
                  </Link>
                  <Link to="/admin/coupons" onClick={() => setMobileOpen(false)} className="p-3 bg-gray-50 rounded-xl text-center text-xs font-bold text-gray-700 hover:bg-purple-50 hover:text-[#6A0DAD] border border-gray-100 transition">
                    <FaTicketAlt className="mx-auto mb-1.5 text-xl opacity-70" /> Kuponlar
                  </Link>
                  <Link to="/admin/campaigns" onClick={() => setMobileOpen(false)} className="col-span-2 p-3 bg-[#6A0DAD]/10 rounded-xl text-center text-xs font-bold text-[#6A0DAD] hover:bg-[#6A0DAD]/20 border border-purple-100 transition flex items-center justify-center gap-2">
                    <FaBullhorn className="text-lg" /> Kampanyalar (Bülten)
                  </Link>
                </div>
              </div>
            )}
          </div>

          {user && (
            <div className="p-5 border-t border-gray-100 bg-gray-50 mt-auto">
              <button
                onClick={handleLogout}
                className="w-full py-3.5 flex items-center justify-center gap-2 text-red-600 font-bold bg-white border border-gray-200 rounded-xl hover:bg-red-50 hover:border-red-100 transition shadow-sm"
              >
                <FaSignOutAlt /> Çıkış Yap
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Navbar;